/**
 * PillarPageShell — shared visual shell for surface pages.
 *
 * Phase 8 Prompt 6 — gives every Phase 5/6 surface a consistent header with
 * pillar accent band per audit 04 §2.1 visual rules (slate banner for
 * Transactional, gold banner for Marketing & Nurture).
 *
 * Subsequent prompts fill in surface-specific content via children.
 */

import React from "react";
import { Box, Typography } from "@mui/material";
import { HelpTip } from "../../help";

export type Pillar = "transactional" | "marketing" | "operate" | "measure";

const PILLAR_CONFIG: Record<
  Pillar,
  { bgColor: string; fgColor: string; label: string; subtitle: string }
> = {
  transactional: {
    bgColor: "#5D6D7E",
    fgColor: "#FFFFFF",
    label: "Build · Transactional / Course-of-Business",
    subtitle:
      "Operational must-deliver · CAN-SPAM exempt · runtime FROZEN per audit constraint",
  },
  marketing: {
    bgColor: "#D4AC0D",
    fgColor: "#1B1F23",
    label: "Build · Marketing & Nurture",
    subtitle:
      "Discretionary outreach · opt-in–gated · CAN-SPAM applies · quiet hours + freq caps enforced",
  },
  operate: {
    bgColor: "#146152",
    fgColor: "#FFFFFF",
    label: "Operate · Live Surveillance",
    subtitle: "Real-time monitoring + live actions across both pillars",
  },
  measure: {
    bgColor: "#1A5F7A",
    fgColor: "#FFFFFF",
    label: "Measure · Analytics",
    subtitle: "Read-only · deep-links back to action surfaces for fixes",
  },
};

export interface PillarPageShellProps {
  pillar: Pillar;
  title: string;
  subtitle?: string;
  helpTipId?: string;
  children?: React.ReactNode;
  /** Optional content for the right-hand side of the title row (e.g. action buttons). */
  rightActions?: React.ReactNode;
}

export const PillarPageShell: React.FC<PillarPageShellProps> = ({
  pillar,
  title,
  subtitle,
  helpTipId,
  children,
  rightActions,
}) => {
  const cfg = PILLAR_CONFIG[pillar];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      {/* Pillar banner */}
      <Box
        sx={{
          bgcolor: cfg.bgColor,
          color: cfg.fgColor,
          borderRadius: 1,
          px: 2,
          py: 1,
          mb: 2,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { sm: "center" },
          justifyContent: "space-between",
          gap: 0.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 700,
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "1.5px",
              lineHeight: 1.2,
            }}
          >
            {cfg.label}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.7rem",
              opacity: 0.85,
              lineHeight: 1.3,
              mt: 0.3,
            }}
          >
            {cfg.subtitle}
          </Typography>
        </Box>
      </Box>

      {/* Title row */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { md: "center" },
          justifyContent: "space-between",
          mb: 2,
          gap: 1,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              color: "#1A5F7A",
              lineHeight: 1.1,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {title}
            {helpTipId && <HelpTip id={helpTipId} size={20} />}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5, maxWidth: 720 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {rightActions && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {rightActions}
          </Box>
        )}
      </Box>

      {children}
    </Box>
  );
};

export default PillarPageShell;
